import React from 'react';
import { Globe, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import i18n from '../i18n';
import TruckLogo from './TruckLogo';

interface LanguageSelectionScreenProps {
  onLanguageSelected: () => void;
}

const LanguageSelectionScreen: React.FC<LanguageSelectionScreenProps> = ({ onLanguageSelected }) => {
  const { changeLanguageAsync, language } = useLanguage();

  const handleSelectLanguage = async (lang: string) => {
    console.log('Language selected:', lang);
    
    try {
      // Save language to localStorage first
      console.log('Saving language to localStorage:', lang);
      localStorage.setItem('cotizatrin_user_language', lang);
      
      // Force page reload to ensure i18n initializes with the correct language
      console.log('Forcing page reload to apply language change');
      window.location.reload();
    } catch (error) {
      console.error('Error changing language:', error);
      // If there's an error, still try to reload
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
        style={{ 
          backgroundImage: 'url("https://images.unsplash.com/photo-1565891741441-64926e441838?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D")',
          backgroundSize: 'cover'
        }}
      >
        <div className="absolute inset-0 bg-black opacity-60"></div>
      </div>

      <div className="w-full max-w-md z-10 px-4">
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="relative h-40 sm:h-48 bg-gradient-to-r from-primary to-primary-dark">
            <div className="absolute inset-0 bg-black opacity-30"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <TruckLogo size={100} className="text-white" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
              <h1 className="text-2xl sm:text-3xl font-bold uppercase text-center">
                COTIZATRIN
              </h1>
              {/* Removed: Cotizador de Transporte de Mercancías Pesadas */}
            </div>
          </div>

          <div className="p-6 text-center bg-gray-50">
            <Globe size={64} className="mx-auto text-primary mb-6" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Select Your Language
            </h2> 
            <p className="text-gray-600 mb-8">
              Choose the language you prefer to use the application
            </p>

            <div className="space-y-4">
              <button
                onClick={() => handleSelectLanguage('es')} // Spanish button
                className={`w-full flex items-center justify-between px-6 py-4 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 ${language === 'es' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              > 
                <div className="flex items-center">
                  <img 
                    src="https://cdn.pixabay.com/photo/2017/01/31/15/13/flag-2024937_1280.png" 
                    alt="Spanish flag" 
                    className="w-8 h-6 mr-4 object-cover rounded shadow-sm" 
                  />
                  <div className="text-left">
                    <div className="text-lg font-bold">Español</div>
                    <div className="text-sm opacity-90">Spanish</div>
                  </div>
                </div>
                {language === 'es' && <Check size={24} className="text-white" />} {/* Checkmark for Spanish */}
              </button>

              <button
                onClick={() => handleSelectLanguage('en')} // English button
                className={`w-full flex items-center justify-between px-6 py-4 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 ${language === 'en' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                <div className="flex items-center">
                  <img 
                    src="https://cdn.pixabay.com/photo/2017/01/31/16/36/banner-2025334_1280.png" 
                    alt="English flag" 
                    className="w-8 h-6 mr-4 object-cover rounded shadow-sm" 
                  />
                  <div className="text-left">
                    <div className="text-lg font-bold">English</div>
                    <div className="text-sm opacity-90">Inglés</div>
                  </div>
                </div>
                {language === 'en' && <Check size={24} className="text-white" />} {/* Checkmark for English */}
              </button>

              <button
                onClick={() => handleSelectLanguage('fr')} // French button
                className={`w-full flex items-center justify-between px-6 py-4 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 ${language === 'fr' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                <div className="flex items-center">
                  <img 
                    src="https://cdn.pixabay.com/photo/2012/04/11/15/19/france-28463_1280.png" 
                    alt="French flag" 
                    className="w-8 h-6 mr-4 object-cover rounded shadow-sm" 
                  />
                  <div className="text-left">
                    <div className="text-lg font-bold">Français</div>
                    <div className="text-sm opacity-90">French</div>
                  </div>
                </div>
                {language === 'fr' && <Check size={24} className="text-white" />} {/* Checkmark for French */}
              </button>

              <button
                onClick={() => handleSelectLanguage('zh')} // Chinese button
                className={`w-full flex items-center justify-between px-6 py-4 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 ${language === 'zh' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                <div className="flex items-center">
                  <img 
                    src="https://cdn.pixabay.com/photo/2015/11/12/16/03/flag-1040555_1280.png" 
                    alt="Chinese flag" 
                    className="w-8 h-6 mr-4 object-cover rounded shadow-sm" 
                  />
                  <div className="text-left">
                    <div className="text-lg font-bold">中文</div>
                    <div className="text-sm opacity-90">Chinese</div>
                  </div>
                </div>
                {language === 'zh' && <Check size={24} className="text-white" />} {/* Checkmark for Chinese */}
              </button>
            </div>

            <div className="mt-8 text-center text-sm text-gray-500">
              <p>© 2025 Cotizatrin. All rights reserved.</p>
              <p className="mt-1">www.cotizatrin.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelectionScreen;